// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import baseTheme from "@insite/mobius/globals/baseTheme";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { TokenExFramePresentationProps } from "@insite/mobius/TokenExFrame";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import ArrowLeft from "@mcmc/XDL/Icons/ArrowLeft";
import ArrowRight from "@mcmc/XDL/Icons/ArrowRight";
import Export from "@mcmc/XDL/Icons/Export";
import Print from "@mcmc/XDL/Icons/Print";
import Question from "@mcmc/XDL/Icons/Question";
import Share from "@mcmc/XDL/Icons/Share";
import { css } from "styled-components";

const robotoFont = "'Roboto', sans-serif, open-sans";
const robotoCondensedFont = "'Roboto Condensed',  sans-serif, open-sans";
const openSansFont = "'Open Sans',  sans-serif, open-sans";
const isMcmcTheme = BLUEPRINT_NAME.toLocaleLowerCase() === "mcmc";
const mcmcFontUrl =
    "https://fonts.googleapis.com/css2?family=Roboto+Condensed:wght@400;700&family=Roboto:wght@400;500;700;900&display=swap";
const trcFontUrl = "https://fonts.googleapis.com/css?family=Open+Sans:400,700&display=swap";

export const Roboto: TypographyPresentationProps = { fontFamily: isMcmcTheme ? robotoFont : openSansFont };
export const RobotoCondensed: TypographyPresentationProps = {
    fontFamily: isMcmcTheme ? robotoCondensedFont : openSansFont,
};
export const fontUrl = isMcmcTheme ? mcmcFontUrl : trcFontUrl;

export const styledScrollbar = (width = 11) => {
    return ({ theme }: any) => css`
        scrollbar-width: thin;
        scrollbar-color: ${resolveColor("common.ui3", theme)} transparent;
        overflow-y: auto;

        &::-webkit-scrollbar {
            width: ${width}px;
        }
        &::-webkit-scrollbar-track {
            background: transparent;
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${resolveColor("common.ui3", theme)};
            border-radius: 6px;
            border: 3px solid transparent;
        }
    `;
};

export const styledLinksMenu = (width: number) => {
    return ({ theme }: any) => css`
        ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}
        padding: 10px 0;
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;
        border-radius: 3px;
        ${StyledClickable} {
            ${/* sc-selector */ StyledTypography} {
                font-size: 14px;
                padding: 10px 15px;
                color: ${resolveColor("text", theme)};
                font-weight: normal;
                text-transform: none;
                letter-spacing: 0;
                width: 100%;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor("common.background", theme)};
                color: ${resolveColor("text", theme)};
                text-decoration: underline;
            }
        }
    `;
};

export const layoutContainer = css`
    @media print {
        max-width: 100% !important;
        margin: 0 !important;
    }

    ${({ theme }: any) => {
        const { maxWidths }: { maxWidths: number[] } = theme.breakpoints || baseTheme.breakpoints;
        const { paddingValues = [15] }: { paddingValues: number[] } = theme.breakpoints;

        const containerStyles = maxWidths.map(
            (maxWidth, index) =>
                css`
                    &:not(.full-width) {
                        max-width: ${maxWidth}px;
                        padding-left: ${paddingValues[index] || paddingValues[0]}px;
                        padding-right: ${paddingValues[index] || paddingValues[0]}px;
                        margin-right: auto;
                        margin-left: auto;
                    }
                    &.full-width {
                        max-width: 100%;
                        margin-right: 0;
                        margin-left: 0;
                        padding-left: 0;
                        padding-right: 0;
                    }
                `,
        );
        return css`
            ${breakpointMediaQueries(theme, containerStyles)}
        `;
    }}
`;

export const hideScrollBar = css`
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
`;

/* <Slider Arrows common styles> */
interface CarouselArrows {
    prev: {
        icon: IconProps;
        clickable: ClickablePresentationProps;
    };
    next: {
        icon: IconProps;
        clickable: ClickablePresentationProps;
    };
}

const arrowClickableStyle = css`
    position: absolute;
    z-index: 1;
    top: 50%;
    transform: translateY(-50%);
    color: ${getColor("common.ui3")};
    opacity: 0.3;
    transition: color 0.2s, opacity 0.2s;
    &:hover:not(:disabled),
    &:focus:not(:disabled) {
        opacity: 1;
        color: ${getColor("common.ui2")};
    }

    &:focus {
        outline: none;
    }
`;

export const carouselArrows: CarouselArrows = {
    prev: {
        icon: { src: ArrowLeft, width: 20, height: 35 },
        clickable: {
            css: css`
                ${arrowClickableStyle}
                margin-left: 10px;
                left: 0;
            `,
        },
    },
    next: {
        icon: { src: ArrowRight, width: 20, height: 35 },
        clickable: {
            css: css`
                ${arrowClickableStyle}
                margin-right: 10px;
                right: 0;
            `,
        },
    },
};
/** </Slider Arrows common styles> */

export const noResultsMessage: TypographyPresentationProps = {
    size: 22,
    lineHeight: "26px",
    weight: 700,
    color: "common.ui2",
    css: css`
        width: 100%;
        min-height: 190px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding-bottom: 20px;
    `,
};

export const spinner: TypographyPresentationProps = {
    size: 22,
    weight: 700,
    color: "common.ui2",
};

export const sectionSubTitleStyle: TypographyPresentationProps = {
    variant: "h6",
    css: css`
        @media print {
            font-size: 18px;
        }
        width: 100%;
        padding-bottom: 10px;
        margin-bottom: 15px;
        border-bottom: 1px solid ${getColor("common.border")};
    `,
};

export const commonBreadcrumbIconStyle = (iconSrc: React.ComponentType<any> | string, iconSize = 16) => ({
    icon: {
        iconProps: {
            src: iconSrc,
            color: "secondary",
            css: css`
                margin: 0;
            `,
        },
    },
    typographyProps: {
        size: iconSize,
        css: css`
            margin: 0;
        `,
    },
    css: css`
        ${getProp("theme.link.defaultProps.css")};
        display: block;
    `,
});

export const printIconStyle: LinkPresentationProps = commonBreadcrumbIconStyle(Print);

export const exportIconStyle: LinkPresentationProps = commonBreadcrumbIconStyle(Export);

export const shareIconStyles: LinkPresentationProps = commonBreadcrumbIconStyle(Share);

export const commonCartButtonStyles = (variant: ButtonVariants): ButtonPresentationProps => ({
    variant,
    sizeVariant: "medium",
    typographyProps: { size: 14 },
    css: css`
        width: 100%;
        margin-bottom: 20px;
        &:last-child {
            margin-bottom: 0;
        }
    `,
});

interface TokenExStyles {
    frame: TokenExFramePresentationProps;
    securityCodeGridItem: GridItemProps;
    securityCodeLink: LinkPresentationProps;
}

export const tokenExStyles: TokenExStyles = {
    frame: {
        sizeVariant: "small",
        cssOverrides: {
            formInputWrapper: css`
                iframe {
                    margin: -8px;
                    height: 51px; //35px input height + 16px negative margins (to save a space for box-shadow-style)
                    width: calc(100% + 16px); //+ 16px to cover the negative margin
                }
            `,
        },
    },
    securityCodeGridItem: {
        css: css`
            position: relative;
            max-width: 110px;
            flex: 1 1 auto;
        `,
    },
    securityCodeLink: {
        color: "secondary",
        icon: {
            iconProps: {
                src: Question,
                size: 14,
                css: css`
                    margin-right: 0;
                `,
            },
        },
        css: css`
            position: absolute;
            top: 10px;
            right: 10px;
        `,
    },
};
